import React from 'react';

import { BOX } from './railLayout';
import { DEFECT, DEFECT_LIGHT, INK, PAPER } from './railPalette';
import { SOUND_ARC_RADII, soundArcPath } from './railSound';
import {
  SIDE_FACES,
  isSideFaceVisible,
  sideFacePoints,
  sideFaceShade,
  topFacePoints,
} from './railTurn';

/**
 * ### BoxVariant
 *
 * A box is either a normal unit or the one with the defect the microphone
 * catches. The defect box is the only colour on the line, and the only box
 * the picker takes off it.
 */
export type BoxVariant = 'normal' | 'defect';

interface IsoBoxProps {
  variant: BoxVariant;
  /** Seconds to offset this box on the shared travel loop, negative to start mid-way */
  delaySeconds: number;
}

/**
 * ### IsoBox
 *
 * One unit on the belt, drawn as a cube with its bottom-front corner at the
 * origin. Three groups deep for a reason: the outer one is carried along
 * the belt by the travel keyframes, the middle one holds the fixed translate
 * that stands the cube on the rail, and the faces inside it are turned on
 * the plate by their own keyframes. The translate has to have a group of
 * its own, because a CSS animation replaces an element's `transform`
 * attribute outright rather than adding to it.
 *
 * Every keyframe under this element reads its delay from `--prl-delay`, so
 * one value staggers the travel, the turn and the sound together. Normal
 * units are outlines filled with the background; the defect is solid red,
 * shaded by which way each face points. The faces on the far side start
 * hidden and come round during the turn. Above the cube, ripples of its
 * sound rise toward the capsule while it is turned: smooth for a good unit,
 * jagged and red for the defect.
 */
export function IsoBox({ variant, delaySeconds }: IsoBoxProps) {
  const isDefect = variant === 'defect';

  return (
    <g
      className={isDefect ? 'prl-box prl-box-defect' : 'prl-box'}
      style={{ '--prl-delay': `${delaySeconds}s` } as React.CSSProperties}
    >
      <g
        transform={`translate(0 ${BOX.baseY})`}
        stroke={isDefect ? '#ffd3da' : INK}
        strokeLinejoin="round"
      >
        {SIDE_FACES.map((face) => (
          <polygon
            key={face.id}
            className={`prl-face prl-face-${face.id}`}
            points={sideFacePoints(face)}
            fill={isDefect ? sideFaceShade(face, 0) : PAPER}
            opacity={isSideFaceVisible(face, 0) ? 1 : 0}
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <polygon
          className="prl-face prl-face-top"
          points={topFacePoints()}
          fill={isDefect ? DEFECT_LIGHT : PAPER}
          vectorEffect="non-scaling-stroke"
        />
        <g
          fill="none"
          stroke={isDefect ? DEFECT : INK}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {SOUND_ARC_RADII.map((radius, index) => (
            <path
              key={radius}
              className={`prl-sound prl-sound-${index}`}
              d={soundArcPath(radius, isDefect)}
              opacity={0}
            />
          ))}
        </g>
      </g>
    </g>
  );
}
